import type { RealtimeToken } from '@soniox/client'
import type { MeetingMinutesData, PreviousAnalysis } from '@ai-meeting/shared'
import type { LiveTranscriptEvent } from './liveTranscriptTurns'

export interface ProxySttEvent extends LiveTranscriptEvent {
  speaker_name?: string
  message?: string
  meeting_id?: string
  data?: PreviousAnalysis | MeetingMinutesData
  code?: string
  realtime_context_chars?: number
  sttProvider?: string
  /**
   * Sent on `ready` — how far into the meeting the server's timeline currently is.
   *
   * Paired with `timeline_resumed`: together they say whether the server adopted
   * the clock this client reported or re-joined one it already held.
   */
  timeline_seed_ms?: number
  /** Sent on `ready` — true when the server re-joined an existing timeline rather than setting one. */
  timeline_resumed?: boolean
  /**
   * Pane switches on `ready` and `features.updated`.
   *
   * Missing on `ready` means this backend does not have the switches.
   */
  features?: unknown
}

export interface ProxyRealtimeToken extends RealtimeToken {
  speaker_name?: string
  translation_latency_ms?: number
  /** When the event carrying this token reached the client — see the same field
   *  on the browser path's token in `useProxySttRecording`. */
  received_ms?: number
  /**
   * End of the segment, the counterpart to the vendor token's `start_ms`.
   *
   * Carried so Input and Output can be lined up segment by segment rather than
   * by a start time alone — see `TRANSCRIPT_TIMINGS_ENABLED`.
   */
  end_ms?: number
}

function secondsToMs(value: number | null | undefined): number | undefined {
  return typeof value === 'number' ? Math.round(value * 1000) : undefined
}

function normalizeSpeaker(speaker: string | number | undefined): string | undefined {
  if (speaker === undefined || speaker === null || speaker === '') return undefined
  return String(speaker)
}

function createToken({
  text,
  isFinal,
  translationStatus,
  speaker,
  speakerName,
  language,
  startTime,
  endTime,
  translationLatencyMs,
}: {
  text: string
  isFinal: boolean
  translationStatus: RealtimeToken['translation_status']
  speaker?: string | number
  speakerName?: string
  language?: string
  startTime?: number | null
  endTime?: number | null
  translationLatencyMs?: number
}): ProxyRealtimeToken {
  return {
    text,
    confidence: 1,
    is_final: isFinal,
    translation_status: translationStatus,
    speaker: normalizeSpeaker(speaker),
    speaker_name: speakerName,
    language,
    start_ms: secondsToMs(startTime),
    end_ms: secondsToMs(endTime),
    translation_latency_ms: translationLatencyMs,
    received_ms: Date.now(),
  }
}

/**
 * The service sends `speaker: null` for an unattributed turn, which every
 * consumer here treats exactly like an absent speaker — so it is normalized to
 * `undefined` in this one place rather than at each call site.
 */
function verifiedSpeaker(
  event: ProxySttEvent,
  fallback?: string | number | null
): string | number | undefined {
  const speaker = event.speaker_name ? event.speaker : (fallback ?? event.speaker)
  return speaker ?? undefined
}

function createTranslationTurnTokens(event: ProxySttEvent): RealtimeToken[] {
  const fullText = event.text
  if (fullText && fullText.trim()) {
    const turns = Array.isArray(event.tokens) ? event.tokens : []
    const speaker = verifiedSpeaker(event, turns[0]?.speaker)
    const language = turns[0]?.language ?? event.language
    return [
      createToken({
        text: fullText,
        isFinal: true,
        translationStatus: 'translation',
        speaker,
        speakerName: event.speaker_name,
        language: language ?? undefined,
        startTime: event.start_time,
        endTime: event.end_time,
        translationLatencyMs: event.translation_latency_ms,
      }),
    ]
  }

  const aggregate = event.translation?.trim()
  if (aggregate) {
    return [
      createToken({
        text: aggregate,
        isFinal: true,
        translationStatus: 'translation',
        speaker: event.speaker ?? undefined,
        speakerName: event.speaker_name,
        language: event.language ?? event.target_lang ?? event.targetLang,
        startTime: event.start_time,
        endTime: event.end_time,
        translationLatencyMs: event.translation_latency_ms,
      }),
    ]
  }

  return []
}

function createTranslationBatchTokens(event: ProxySttEvent): RealtimeToken[] {
  const turns = Array.isArray(event.translation_turns) ? event.translation_turns : []
  const turnTokens = turns
    .filter((turn) => turn.text?.trim())
    .map((turn) =>
      createToken({
        text: turn.text ?? '',
        isFinal: true,
        translationStatus: 'translation',
        speaker: verifiedSpeaker(event, turn.speaker),
        speakerName: event.speaker_name,
        language: event.target_lang ?? event.targetLang,
        startTime: event.start_time,
        endTime: event.end_time,
        translationLatencyMs: event.translation_latency_ms,
      })
    )
  if (turnTokens.length > 0) return turnTokens

  const aggregate = event.translation?.trim()
  if (aggregate) {
    return [
      createToken({
        text: aggregate,
        isFinal: true,
        translationStatus: 'translation',
        speaker: event.speaker ?? undefined,
        speakerName: event.speaker_name,
        language: event.target_lang ?? event.targetLang,
        startTime: event.start_time,
        endTime: event.end_time,
        translationLatencyMs: event.translation_latency_ms,
      }),
    ]
  }

  return []
}

export function proxyEventToTokens(event: ProxySttEvent): {
  finalTokens: RealtimeToken[]
  partialTokens: RealtimeToken[]
  batchTokens: RealtimeToken[]
  clearPartial?: boolean
  partialStatus?: RealtimeToken['translation_status']
} {
  switch (event.event) {
    case 'transcript.partial':
      return {
        finalTokens: [],
        partialStatus: 'original',
        batchTokens: [],
        clearPartial: !event.text,
        partialTokens: event.text
          ? [
              createToken({
                text: event.text,
                isFinal: false,
                translationStatus: 'original',
                speaker: event.speaker ?? undefined,
                speakerName: event.speaker_name,
                language:
                  event.tokens?.[0]?.language ??
                  event.language ??
                  event.source_lang ??
                  event.sourceLang ??
                  undefined,
                startTime: event.start_time,
                endTime: event.end_time,
              }),
            ]
          : [],
      }

    case 'transcript.final':
      return {
        clearPartial: true,
        partialStatus: 'original',
        partialTokens: [],
        batchTokens: [],
        finalTokens: event.text
          ? [
              createToken({
                text: event.text,
                isFinal: true,
                translationStatus: 'original',
                speaker: event.speaker ?? undefined,
                speakerName: event.speaker_name,
                language:
                  event.tokens?.[0]?.language ??
                  event.language ??
                  event.source_lang ??
                  event.sourceLang ??
                  undefined,
                startTime: event.start_time,
                endTime: event.end_time,
              }),
            ]
          : [],
      }

    case 'translation.partial':
      return {
        finalTokens: [],
        batchTokens: [],
        partialStatus: 'translation',
        clearPartial: !event.text,
        partialTokens: event.text
          ? [
              createToken({
                text: event.text,
                isFinal: false,
                translationStatus: 'translation',
                speaker: event.speaker ?? undefined,
                speakerName: event.speaker_name,
                language:
                  event.tokens?.[0]?.language ??
                  event.language ??
                  event.target_lang ??
                  event.targetLang ??
                  undefined,
                startTime: event.start_time,
                endTime: event.end_time,
                translationLatencyMs: event.translation_latency_ms,
              }),
            ]
          : [],
      }

    case 'translation.final':
      return {
        clearPartial: true,
        partialStatus: 'translation',
        partialTokens: [],
        batchTokens: [],
        finalTokens: createTranslationTurnTokens(event),
      }

    case 'translation.batch':
      return {
        partialTokens: [],
        finalTokens: [],
        batchTokens: createTranslationBatchTokens(event),
      }

    default:
      return { finalTokens: [], partialTokens: [], batchTokens: [] }
  }
}
